/**
 * EnrollmentsCard — humanized automation enrollments (+ the super-admin test
 * re-enroll). Moved verbatim from ContactRail's Next-steps tab to the Phase G
 * Profile tab: enrollment state is contact history, not a next step.
 */
import { Loader2, Send, Workflow } from 'lucide-react';
import { Enrollment } from '../../types';
import { relTime } from '../../lib';

function enrollmentPill(status: string): string {
  if (status === 'active') return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
  if (status === 'completed') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

interface Props {
  enrollments: Enrollment[];
  isSuperAdmin: boolean;
  reenrolling: boolean;
  onReEnroll: () => void;
  /** Rendered inside ProfileTab's frameless grouped list, which owns the
   *  divide-y chrome: drop this card's own border/background/shadow. Absent =
   *  the standalone card, byte for byte what renders today. */
  frameless?: boolean;
}

export default function EnrollmentsCard({ enrollments, isSuperAdmin, reenrolling, onReEnroll, frameless = false }: Props) {
  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6'}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Workflow className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Enrollments
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({enrollments.length})</span>
        </h3>
        {isSuperAdmin && (
          <button
            onClick={onReEnroll}
            disabled={reenrolling}
            title="Re-fire the welcome flow for this test contact (no new lead)"
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
          >
            {reenrolling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {reenrolling ? 'Re-enrolling…' : 'Re-enroll (test)'}
          </button>
        )}
      </div>
      {enrollments.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Not enrolled in any flow.</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {enrollments.map((e) => (
            <div key={e.id} className="py-2.5" title={`Flow v${e.flow_version} · node ${e.current_node_id || '—'}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {e.flow_name || 'Automation flow'}
                </span>
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${enrollmentPill(e.status)}`}>
                  {e.status}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-gray-400">
                {e.next_run_at
                  ? `Next step ${relTime(e.next_run_at)}`
                  : e.completed_at
                  ? `Completed ${relTime(e.completed_at)}`
                  : `Enrolled ${relTime(e.enrolled_at)}`}
              </p>
              {e.last_error && <p className="mt-0.5 break-words text-xs text-red-500">{e.last_error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
